"use client"

import { type ReactNode, useEffect, useRef, useState } from "react"
import Image from "next/image"
import { m } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { MRI_WALKTHROUGH_STEPS } from "@/data/mri-sections"
import { PlaceholderMedia } from "@/components/ui/placeholder-media"
import { blobMediaUrl } from "@/lib/blob-url"

/** A walkthrough step with its copy already resolved to display strings. */
export interface WalkthroughStep {
  title: string
  body: string
  placeholderLabel: string
  /** Blob path passed to `blobMediaUrl()`. When unset, shows the placeholder. */
  image?: string
  /** Optional CTAs rendered under the body — e.g. the booking buttons on
   *  step 1 of the gender-health walkthrough. */
  actions?: ReactNode
}

function StepMedia({
  step,
  priority,
  sizes,
}: {
  step: WalkthroughStep
  priority?: boolean
  sizes: string
}) {
  if (!step.image) {
    return (
      <PlaceholderMedia
        label={step.placeholderLabel}
        className="h-full w-full"
      />
    )
  }
  return (
    <Image
      src={blobMediaUrl(step.image)}
      alt=""
      fill
      priority={priority}
      sizes={sizes}
      className="object-cover"
    />
  )
}

const EXPO = [0.16, 1, 0.3, 1] as const

/**
 * "How it works" — scroll-linked visit walkthrough. A clickable connected
 * stepper: numbered nodes on a connector line that fills to the active step,
 * with a detail panel below. The active step advances automatically as the
 * section scrolls past (sticky scroll-linked); clicking a node jumps the page
 * to that step. Mobile: a plain stacked list.
 *
 * Defaults to the full-body-MRI 5-step visit walkthrough (the `mriWalk*` keys
 * + `MRI_WALKTHROUGH_STEPS`). Pass `steps` plus `label`/`heading`/`stepWord`
 * (already-resolved strings) to reuse it on another surface — e.g. the
 * gender-health MRI add-on pages feed their own 3 steps and per-page heading.
 */
export function VisitWalkthroughTimeline({
  steps: stepsProp,
  label: labelProp,
  heading: headingProp,
  stepWord: stepWordProp,
}: {
  steps?: WalkthroughStep[]
  label?: string
  heading?: string
  stepWord?: string
} = {}) {
  const tt = useTranslations("tests") as unknown as (k: string) => string
  const steps: WalkthroughStep[] =
    stepsProp ??
    MRI_WALKTHROUGH_STEPS.map((s) => ({
      title: tt(`${s.keyBase}Title`),
      body: tt(`${s.keyBase}Body`),
      placeholderLabel: s.placeholderLabel,
      image: s.image,
    }))
  const label = labelProp ?? tt("mriWalkLabel")
  const heading = headingProp ?? tt("mriWalkHeading")
  const stepWord = stepWordProp ?? tt("mriWalkStepWord")
  const total = steps.length
  const [activeStep, setActiveStep] = useState(0)
  const sectionRef = useRef<HTMLDivElement>(null)

  // Scroll progress through the tall section drives the active step.
  useEffect(() => {
    const section = sectionRef.current
    if (!section) return
    let raf = 0
    const compute = () => {
      raf = 0
      const rect = section.getBoundingClientRect()
      const scrollRange = section.offsetHeight - window.innerHeight
      if (scrollRange <= 0) return
      const p = Math.max(0, Math.min(1, -rect.top / scrollRange))
      setActiveStep(Math.min(total - 1, Math.floor(p * total)))
    }
    const onScroll = () => {
      if (raf === 0) raf = requestAnimationFrame(compute)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    compute()
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [total])

  // Click a node → scroll the page to the middle of that step's zone.
  const jumpTo = (i: number) => {
    const section = sectionRef.current
    if (!section) return
    const scrollRange = section.offsetHeight - window.innerHeight
    const sectionTop = section.getBoundingClientRect().top + window.scrollY
    window.scrollTo({
      top: sectionTop + ((i + 0.5) / total) * scrollRange,
      behavior: "smooth",
    })
  }

  const step = steps[activeStep]
  // Fill snaps to the active node (so clicking node i stops the line exactly at
  // it), animated via the width transition on the fill bar below.
  const fillPct = total > 1 ? (activeStep / (total - 1)) * 100 : 0

  return (
    <div id="mri-walkthrough" className="bg-background py-3 md:py-4">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* Mobile: stacked, no sticky — inside the grey panel */}
        <section className="rounded-[24px] bg-white py-16 md:hidden md:rounded-[32px]">
          <div className="mx-auto w-full max-w-[1440px] px-4 text-center">
            <p className="type-label-lg mb-3 text-neutral-500 uppercase">
              {label}
            </p>
            <h2 className="type-display-md text-neutral-900">{heading}</h2>
          </div>
          <div className="mx-auto mt-10 flex w-full max-w-[1440px] flex-col gap-12 px-4">
            {steps.map((s, i) => (
              <div key={i}>
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px] bg-neutral-200">
                  <StepMedia step={s} priority={i === 0} sizes="100vw" />
                </div>
                <h3 className="type-title-md mt-4 text-neutral-900">
                  {s.title}
                </h3>
                <p className="type-body-md mt-2 text-neutral-600">{s.body}</p>
                {s.actions && <div className="mt-6">{s.actions}</div>}
              </div>
            ))}
          </div>
        </section>

        {/* Desktop: sticky scroll-linked clickable stepper */}
        <section
          ref={sectionRef}
          className="relative hidden rounded-[24px] bg-white md:block md:rounded-[32px]"
          style={{ minHeight: `${total * 100}vh` }}
        >
          <div className="sticky top-0 flex h-screen flex-col justify-center py-16">
            <div className="mx-auto mb-10 w-full max-w-[620px] px-4 text-center md:mb-12">
              <p className="type-label-lg mb-3 text-neutral-500 uppercase">
                {label}
              </p>
              <h2 className="type-display-md text-neutral-900">{heading}</h2>
            </div>

            <div className="mx-auto w-full max-w-[1100px] px-4 md:px-10">
              {/* Stepper rail */}
              <div className="relative mx-[10%]">
                <div className="absolute top-1/2 right-0 left-0 h-0.5 -translate-y-1/2 rounded-full bg-neutral-200" />
                <div
                  className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 rounded-full bg-neutral-900 transition-[width] duration-300 ease-out"
                  style={{ width: `${fillPct}%` }}
                />
                <div className="relative flex justify-between">
                  {steps.map((s, i) => {
                    const done = i <= activeStep
                    return (
                      <button
                        key={i}
                        type="button"
                        data-testid="walk-node"
                        aria-current={i === activeStep}
                        aria-label={s.title}
                        onClick={() => jumpTo(i)}
                        className={cn(
                          "flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[13px] leading-none font-medium ring-4 ring-white transition-[transform,background-color,color] duration-300", // ds-typo-ignore
                          done
                            ? "bg-neutral-900 text-white"
                            : "bg-white text-neutral-900 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.18)]",
                          i === activeStep && "scale-110"
                        )}
                      >
                        {i + 1}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Detail panel — swaps with the active step */}
              <m.div
                key={activeStep}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.45,
                  ease: EXPO as unknown as number[],
                }}
                className="mt-12 grid items-center gap-10 md:grid-cols-2 md:gap-16"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px] bg-neutral-200">
                  <StepMedia
                    step={step}
                    priority={false}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                  />
                </div>
                <div>
                  <p className="type-label-sm bg-deep-green-50 text-deep-green-700 inline-flex items-center rounded-full px-3 py-1 uppercase">
                    {stepWord} {String(activeStep + 1).padStart(2, "0")}
                  </p>
                  <h3 className="type-display-sm mt-3 text-neutral-900">
                    {step.title}
                  </h3>
                  <p className="type-body-lg mt-3 text-neutral-600">
                    {step.body}
                  </p>
                  {step.actions && <div className="mt-6">{step.actions}</div>}
                </div>
              </m.div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
